import React, { useMemo } from 'react';
import Modal from './Modal';
import { ModalProps } from '../../types/ui';
import { useResponsive } from '../../hooks';

interface FormModalProps extends Omit<ModalProps, 'children'> {
  onSubmit: (e: React.FormEvent) => void;
  children: React.ReactNode;
  submitText?: string;
  cancelText?: string;
  isSubmitting?: boolean;
  error?: string | null;
  hideButtons?: boolean;
  formId?: string;
}

/**
 * Standardized modal component for forms across the application
 * Updated to be consistent with TransactionDetailModal pattern
 */
const FormModal: React.FC<FormModalProps> = ({
  onSubmit,
  children,
  submitText = 'Kaydet',
  cancelText = 'İptal',
  isSubmitting = false,
  error = null,
  hideButtons = false,
  formId: propFormId,
  ...modalProps
}) => {
  const { isSmallerThan } = useResponsive();
  const isMobile = isSmallerThan('md');
  
  // Generate a unique ID for the form if not provided
  const formId = useMemo(() => propFormId || `form-modal-${Math.random().toString(36).substring(2, 9)}`, [propFormId]);

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault(); // Prevent default form submission (page reload)
    onSubmit(e); // Call the original onSubmit handler passed as a prop
  };

  return (
    <Modal {...modalProps} id={formId}>
      <form id={formId} onSubmit={handleFormSubmit} className="space-y-4">
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            {error}
          </div>
        )}
        
        {children}
        
        {!hideButtons && !isMobile && (
          <div className="flex items-center justify-end space-x-4 mt-6 pt-4 border-t border-primary-silver/30">
            <button
              type="button"
              onClick={modalProps.onClose}
              className="px-4 py-2 text-sm font-medium text-primary-charcoal bg-white border border-primary-silver rounded-md shadow-sm hover:bg-primary-silver/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-charcoal"
              disabled={isSubmitting}
            >
              {cancelText}
            </button>
            <button
              type="submit"
              className="px-4 py-2 text-sm font-medium text-accent-white bg-primary-charcoal border border-transparent rounded-md shadow-sm hover:bg-primary-charcoal/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-charcoal"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'İşleniyor...' : submitText}
            </button>
          </div>
        )}
      </form>

      {/* Mobile Bottom Action Bar */}
      {!hideButtons && isMobile && (
        <div className="sticky bottom-0 left-0 right-0 bg-white border-t border-primary-silver/30 p-4 mt-4">
          {modalProps.onClose && (
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={modalProps.onClose}
                className="px-4 py-2 text-sm font-medium text-primary-charcoal bg-white border border-primary-silver rounded-md shadow-sm hover:bg-primary-silver/10 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-charcoal"
                disabled={isSubmitting}
              >
                {cancelText}
              </button>
              <button
                type="submit"
                form={formId}
                className="px-4 py-2 text-sm font-medium text-accent-white bg-primary-charcoal border border-transparent rounded-md shadow-sm hover:bg-primary-charcoal/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-charcoal"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'İşleniyor...' : submitText}
              </button>
            </div>
          )}
          {!modalProps.onClose && (
            <button
              type="submit"
              form={formId}
              className="w-full px-4 py-2 text-sm font-medium text-accent-white bg-primary-charcoal border border-transparent rounded-md shadow-sm hover:bg-primary-charcoal/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-charcoal"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'İşleniyor...' : submitText}
            </button>
          )}
        </div>
      )}
    </Modal>
  );
};

export default FormModal; 